/**
 * Portal Analytics API service.
 *
 * Calls the admin portal-analytics endpoints to fetch
 * aggregated interaction data for the dashboard.
 */

import api from './api';

// --- Response types ---

export interface EventTypeStat {
  event_type: string;
  count: number;
}

export interface TopTarget {
  event_target: string;
  count: number;
}

export interface TopPage {
  page: string;
  count: number;
}

export interface OverviewStats {
  total_events: number;
  active_users: number;
  unique_sessions: number;
  event_type_breakdown: EventTypeStat[];
  top_targets: TopTarget[];
  top_pages: TopPage[];
  period_start: string;
  period_end: string;
}

export interface UserActivity {
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  total_events: number;
  sessions: number;
  first_seen: string;
  last_seen: string;
  top_event_type: string;
}

export interface UserActivityResponse {
  users: UserActivity[];
  total_users: number;
}

export interface TimelineBucket {
  bucket: string;
  count: number;
  unique_users: number;
}

export interface TimelineResponse {
  buckets: TimelineBucket[];
  granularity: string;
}

export interface SequenceTransition {
  from_page: string;
  to_page: string;
  count: number;
  avg_dwell_seconds: number | null;
}

export interface JourneyEvent {
  event_type: string;
  event_target: string;
  page: string;
  timestamp: string;
}

export interface UserJourney {
  user_name: string;
  user_email: string;
  session_id: string;
  events: JourneyEvent[];
  started_at: string;
  duration_seconds: number;
}

export interface SequenceResponse {
  transitions: SequenceTransition[];
  recent_journeys: UserJourney[];
}

export interface PatternStep {
  event_type: string;
  page: string;
}

export interface CommonPattern {
  pattern: PatternStep[];
  pattern_display: string;
  session_count: number;
  unique_users: number;
  example_users: string[];
  avg_duration_seconds: number | null;
  avg_events_per_session: number | null;
}

export interface CommonPatternsResponse {
  patterns: CommonPattern[];
  total_sessions_analyzed: number;
}

export interface AnalyticsUser {
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
}

export interface AnalyticsUsersResponse {
  users: AnalyticsUser[];
}

// --- Feature adoption (audit truth tables, not portal_events) ---

export interface FieldCount {
  field_name: string;
  count: number;
}

export interface EditorActivity {
  user_id: string;
  user_name: string;
  user_email: string;
  header_edits: number;
  line_edits: number;
  orders_touched: number;
  last_edit_at: string | null;
}

export interface OrderEditDay {
  day: string;
  header_edits: number;
  line_edits: number;
}

export interface OrderEditingStats {
  header_edits: number;
  line_field_edits: number;
  lines_added: number;
  lines_removed: number;
  orders_touched: number;
  editors: number;
  top_fields: FieldCount[];
  by_user: EditorActivity[];
  daily: OrderEditDay[];
}

export interface SageWriteKindStats {
  kind: string;
  total: number;
  confirmed: number;
  failed: number;
  in_flight: number;
  success_rate: number | null;
  avg_latency_ms: number | null;
}

export interface SourceCount {
  source: string;
  count: number;
}

export interface SageWriteFailure {
  write_id: string;
  kind: string;
  source: string;
  sage_order_no: string | null;
  entity_ref: string | null;
  error_message: string | null;
  user_name: string | null;
  created_at: string;
  failed_at: string | null;
}

export interface SageWriteDay {
  day: string;
  confirmed: number;
  failed: number;
  in_flight: number;
}

export interface EditInSageStats {
  total_writes: number;
  confirmed: number;
  failed: number;
  in_flight: number;
  success_rate: number | null;
  latency_avg_ms: number | null;
  latency_p95_ms: number | null;
  by_kind: SageWriteKindStats[];
  by_source: SourceCount[];
  recent_failures: SageWriteFailure[];
  daily: SageWriteDay[];
}

/** interactive = a person types the turns; headless = scheduled / pipeline agent; unknown = retired id. */
export type CopilotProfileKind = 'interactive' | 'headless' | 'unknown';

export interface CopilotProfileStats {
  profile_id: string;
  kind: CopilotProfileKind;
  conversations: number;
  human_turns: number;
  automated_turns: number;
  users: number;
}

export interface CopilotUserStats {
  user_id: string;
  user_name: string;
  user_email: string;
  conversations: number;
  human_turns: number;
  last_active_at: string | null;
  profiles: string[];
}

export interface CopilotDay {
  day: string;
  human_turns: number;
  automated_turns: number;
}

export interface CopilotStats {
  human_turns: number;
  automated_turns: number;
  conversations: number;
  active_users: number;
  by_profile: CopilotProfileStats[];
  by_user: CopilotUserStats[];
  daily: CopilotDay[];
}

export interface KirkDraftingDay {
  day: string;
  drafted: number;
  failed: number;
  human_sends: number;
  sends_from_drafts: number;
  gate_filtered: number;
}

export interface KirkGateFilteredThread {
  email_thread_id: string;
  subject: string | null;
  from_address: string | null;
  gate_reason: string | null;
  gate_confidence: number | null;
  filtered_at: string;
  human_replied_at: string | null;
}

export interface KirkDraftingStats {
  runs_total: number;
  drafted: number;
  skipped: number;
  failed: number;
  open: number;
  /** drafted / (drafted + agent_skipped + failed): of the mail the agent saw. Gate-filtered mail excluded. */
  draft_rate: number | null;
  gate_filtered: number;
  agent_skipped: number;
  gate_fail_open: number;
  gate_filtered_then_replied: number;
  gate_filtered_threads: KirkGateFilteredThread[];
  human_sends: number;
  sends_from_drafts: number;
  avg_similarity: number | null;
  sent_verbatim: number;
  daily: KirkDraftingDay[];
}

export interface FeatureAdoptionResponse {
  period_start: string;
  period_end: string;
  days: number;
  user_filter_applied: boolean;
  order_editing: OrderEditingStats;
  edit_in_sage: EditInSageStats;
  copilot: CopilotStats;
  kirk_drafting: KirkDraftingStats;
}

// --- Service ---

export const portalAnalyticsService = {
  getOverview: async (days = 7, userId?: string): Promise<OverviewStats> => {
    const resp = await api.get<OverviewStats>('/admin/portal-analytics/overview', {
      params: { days, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },

  getUserActivity: async (days = 7, userId?: string): Promise<UserActivityResponse> => {
    const resp = await api.get<UserActivityResponse>('/admin/portal-analytics/users', {
      params: { days, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },

  getTimeline: async (days = 7, granularity: 'hour' | 'day' = 'hour', userId?: string): Promise<TimelineResponse> => {
    const resp = await api.get<TimelineResponse>('/admin/portal-analytics/timeline', {
      params: { days, granularity, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },

  getSequences: async (days = 7, limit = 10, userId?: string): Promise<SequenceResponse> => {
    const resp = await api.get<SequenceResponse>('/admin/portal-analytics/sequences', {
      params: { days, limit, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },

  getCommonPatterns: async (days = 7, minSessions = 1, userId?: string): Promise<CommonPatternsResponse> => {
    const resp = await api.get<CommonPatternsResponse>('/admin/portal-analytics/common-patterns', {
      params: { days, min_sessions: minSessions, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },

  getActiveUsers: async (days = 90): Promise<AnalyticsUsersResponse> => {
    const resp = await api.get<AnalyticsUsersResponse>('/admin/portal-analytics/active-users', {
      params: { days },
    });
    return resp.data;
  },

  getFeatureAdoption: async (days = 7, userId?: string): Promise<FeatureAdoptionResponse> => {
    const resp = await api.get<FeatureAdoptionResponse>('/admin/portal-analytics/feature-adoption', {
      params: { days, ...(userId && { user_id: userId }) },
    });
    return resp.data;
  },
};
